import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useAuth } from '../lib/auth/auth-context';
import { supabase as defaultClient } from '../lib/supabase';
import { captureSupabaseError } from '../lib/sentry-supabase';
import type { LynrxSupabaseClient } from '@lynrx/shared';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { Modal } from '../components/ui/Modal';
import { DataTable, type Column } from '../components/ui/DataTable';
import { StatusBadge } from '../components/ui/StatusBadge';
import { useSetPageHeader } from '../components/layout/PageHeaderContext';
import { OrderQuickView } from '../components/orders/OrderQuickView';

// DB enum values from order_status (database.types.ts). Display labels
// are title-cased forms of the same strings — no mapping layer per
// Decision 11 follow-up (see PR description for the divergence note).
export const ORDER_STATUS_VALUES = [
  'pending',
  'assigned',
  'out_for_delivery',
  'delivered',
  'attempted',
  'returned',
  'cancelled',
  'failed',
] as const;

export type OrderStatusValue = (typeof ORDER_STATUS_VALUES)[number];
export type StatusFilter = 'all' | OrderStatusValue;

const STATUS_FILTER_VALUES: readonly StatusFilter[] = ['all', ...ORDER_STATUS_VALUES];

// Terminal statuses — bulk actions cannot affect orders in these states,
// so checkbox is disabled. Order-detail brief lists delivered as
// terminal; we add returned and cancelled because none of the bulk
// RPCs accept them as input states. (The Create route flow has always
// required pending; that constraint moves into the action-button
// preflight instead of the row-level checkbox.)
//
// `delivered` is intentionally NOT in this set — the Retrieval
// (recall from customer) bulk action operates on delivered orders,
// so rows in that status still need to be selectable. The
// per-action modals show upfront eligibility so a delivered row
// selected alongside Return to Pharmacy just skips at server time.
const TERMINAL_STATUSES = new Set(['returned', 'cancelled']);

function isStatusFilter(value: unknown): value is StatusFilter {
  return (
    typeof value === 'string' &&
    (STATUS_FILTER_VALUES as readonly string[]).includes(value)
  );
}

export function statusLabel(value: StatusFilter | string): string {
  if (value === 'all') return 'All';
  const words = value.split('_');
  const first = words[0];
  if (!first) return value;
  const head = first.charAt(0).toUpperCase() + first.slice(1);
  return [head, ...words.slice(1)].join(' ');
}

// Filter state lives in the URL so dispatchers can bookmark / share
// "delivered orders for driver X, Mon-Wed". Search text stays in local
// state — search includes order_number which is PHI-adjacent per
// CLAUDE.md and shouldn't end up in URL bars or telemetry.
type OrdersSearch = {
  status: StatusFilter;
  // 'all' | driver user-id. Optional in the URL type so callers
  // (other routes navigating to /orders) don't have to specify it.
  // validateSearch normalizes missing → 'all'.
  driver?: string;
  // Single date (YYYY-MM-DD, local-calendar). validateSearch defaults
  // missing → today. CNC's Dashboard pattern: day-by-day stepper, not
  // a date range. The /audit page still uses date ranges; /orders is
  // a "today's deliveries" tracking surface.
  date?: string;
  // 'all' | city literal. Populated from distinct values in the
  // visible day's orders.
  city?: string;
  // 'all' | ZIP literal.
  zip?: string;
};

// Patch shape for the filter-row setSearch callback. Distinct from
// OrdersSearch because we want `null` to mean "clear this field" —
// exactOptionalPropertyTypes forbids assigning `undefined` directly,
// and `null` carries the explicit intent.
type OrdersSearchPatch = {
  status?: StatusFilter;
  driver?: string;
  date?: string | null;
  city?: string | null;
  zip?: string | null;
};

function isIsoDateString(v: unknown): v is string {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
}

function todayIso(): string {
  // Local-calendar YYYY-MM-DD. UTC midnight would tip the date backward
  // in negative offsets. Same helper shape as /dispatch + /schedule.
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export const Route = createFileRoute('/_authenticated/orders/')({
  component: OrdersListPage,
  validateSearch: (search: Record<string, unknown>): OrdersSearch => {
    const rawStatus = search['status'];
    const rawDriver = search['driver'];
    const rawDate = search['date'];
    const rawCity = search['city'];
    const rawZip = search['zip'];
    const out: OrdersSearch = {
      status: isStatusFilter(rawStatus) ? rawStatus : 'all',
      driver: typeof rawDriver === 'string' && rawDriver.length > 0 ? rawDriver : 'all',
      date: isIsoDateString(rawDate) ? rawDate : todayIso(),
    };
    if (typeof rawCity === 'string' && rawCity.length > 0) out.city = rawCity;
    if (typeof rawZip === 'string' && rawZip.length > 0) out.zip = rawZip;
    return out;
  },
});

const PAGE_SIZE = 50;

export type OrderListRow = {
  id: string;
  order_number: string;
  patient_name: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  pharmacy_id: string;
  dropoff_stop: {
    address: string;
    city: string | null;
    zip: string | null;
    route: {
      id: string;
      driver: { full_name: string | null; email: string } | null;
    } | null;
  } | null;
  // POD record for this order — null when the driver hasn't recorded
  // delivery yet (or status is pre-delivery). Feeds the CHECKS column
  // (G/B/P circles) and the POD link column.
  pod_records: {
    id: string;
    barcode_scanned: boolean;
    photo_paths: string[];
  }[];
  // Delivery row carries the geofence-override flag (server-side
  // computation in complete_delivery; not stored on pod_records).
  // Array shape because PostgREST returns array for the one-to-many
  // embed; in practice it's 0 or 1 row per order.
  deliveries: {
    id: string;
    delivery_geofence_overridden: boolean;
  }[];
};

type DriverOption = {
  id: string;
  full_name: string | null;
  email: string;
};

type OrderCounts = {
  total: number;
  delivered: number;
  pending: number;
  failed: number;
};

const ATTENTION_STATUSES = ['failed', 'attempted', 'returned'] as const;

// Split the orders select string out so the driver-filter branch can
// flip the dropoff_stop/route embeds to !inner without diverging the
// rest of the select.
function buildOrdersSelect(innerJoinForDriver: boolean): string {
  const stopMod = innerJoinForDriver ? '!inner' : '';
  const routeMod = innerJoinForDriver ? '!inner' : '';
  return (
    'id, order_number, patient_name, status, created_at, updated_at, pharmacy_id, ' +
    `dropoff_stop:stops!orders_dropoff_stop_id_fkey${stopMod}(address, city, zip, ` +
    `route:routes!stops_route_id_fkey${routeMod}(id, driver:users!routes_driver_id_fkey(full_name, email))), ` +
    // POD-evidence joins for the CHECKS column + POD link column.
    // Both are one-to-many in the schema but in practice 0 or 1 rows
    // per order; we collapse client-side.
    'pod_records(id, barcode_scanned, photo_paths), ' +
    'deliveries(id, delivery_geofence_overridden)'
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// CNC-parity title-caser. Source data comes in mixed casing — some
// rows stored as ALL CAPS ("HUDY, THOMAS J"), others already title-
// cased. CNC normalizes on render (~/Desktop/cnc-web src/lib/
// normalizeCity.js:27). We apply it here so the visible table reads
// as pharmacy formatting ("Hudy, Thomas J") regardless of ingest
// source. Lowercases everything first, then upper-cases the first
// letter of each word — with a small stop-word list (of/the/and/a/
// an/in/on) that stays lower-case when not the leading word.
const TITLE_CASE_LOWER = new Set(['of', 'the', 'and', 'a', 'an', 'in', 'on']);
function titleCase(str: string): string {
  return str.toLowerCase().replace(/[\p{L}\p{N}']+/gu, (word, idx) => {
    if (idx > 0 && TITLE_CASE_LOWER.has(word)) return word;
    return word.charAt(0).toUpperCase() + word.slice(1);
  });
}

function addressDisplay(row: OrderListRow): string {
  const stop = row.dropoff_stop;
  if (!stop) return '—';
  return titleCase(stop.address);
}

function cityDisplay(row: OrderListRow): string {
  const city = row.dropoff_stop?.city;
  return city ? titleCase(city) : '—';
}

function zipDisplay(row: OrderListRow): string {
  return row.dropoff_stop?.zip ?? '—';
}

// Shared first-name extractor for driver labels. Used by both the
// Driver column render and the Driver filter dropdown so the two
// surfaces stay in sync. Falls back to the email local-part if no
// full_name is present ("adam@cncdelivery.com" → "adam").
function firstNameOf(source: string): string {
  const firstToken = source.trim().split(/\s+/)[0] ?? source;
  const emailIdx = firstToken.indexOf('@');
  return emailIdx >= 0 ? firstToken.slice(0, emailIdx) : firstToken;
}

function driverDisplay(row: OrderListRow): string {
  const d = row.dropoff_stop?.route?.driver;
  if (!d) return '—';
  // First name only — CNC-parity for the Orders table (their pharmacy
  // portal shows "Bobby", "Brad", "Adam" not "Dominic Cutrufello").
  return firstNameOf(d.full_name ?? d.email);
}

// "Time" — for delivered orders we show updated_at (which the order
// status trigger bumps when status flips to delivered). For everything
// else show the created_at — that's what dispatchers actually want as
// a single time column. Matches CNC's "Time" semantics for pending
// rows (when the order was queued).
//
// Format is time-of-day only (e.g. "10:45 PM"). The page header
// already shows the full date being viewed, so repeating "Aug 13,
// 2026, " on every row wraps the cell to 3 lines and wastes vertical
// space. Dropping the date collapses each row to a single line.
function timeDisplay(row: OrderListRow): string {
  const iso = row.status === 'delivered' ? row.updated_at : row.created_at;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

// CHECKS column — G (geofence pass) / B (barcode) / P (photo). Each
// circle is filled green when present, ghosted gray when absent.
// Renders "—" when there's no POD record yet (pre-delivery rows).
function checksDisplay(row: OrderListRow): JSX.Element {
  const pod = row.pod_records[0];
  const delivery = row.deliveries[0];
  if (!pod) {
    return <span className="text-ink-3">—</span>;
  }
  const geofenceOk = delivery ? !delivery.delivery_geofence_overridden : false;
  const barcodeOk = pod.barcode_scanned;
  const photoOk = pod.photo_paths && pod.photo_paths.length > 0;
  return (
    <div className="flex items-center gap-1">
      <CheckCircle letter="G" present={geofenceOk} title="Geofence pass" />
      <CheckCircle letter="B" present={barcodeOk} title="Barcode scanned" />
      <CheckCircle letter="P" present={photoOk} title="Photo captured" />
    </div>
  );
}

function CheckCircle({
  letter,
  present,
  title,
}: {
  letter: string;
  present: boolean;
  title: string;
}): JSX.Element {
  const cls = present
    ? 'bg-signal-green text-white'
    : 'bg-bg-soft text-ink-3';
  return (
    <span
      title={`${title}: ${present ? 'yes' : 'no'}`}
      className={
        'inline-flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-semibold ' +
        cls
      }
    >
      {letter}
    </span>
  );
}

// POD column — "POD" button linking to the order detail (where the
// existing POD viewer lives) when a pod_record exists. "—" otherwise.
function podDisplay(row: OrderListRow): JSX.Element {
  if (row.pod_records.length === 0) {
    return <span className="text-ink-3">—</span>;
  }
  return (
    <Link
      to="/orders/$orderId"
      params={{ orderId: row.id }}
      search={{ status: 'all' as StatusFilter }}
      className="rounded-md border border-border bg-bg px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-ink-2 hover:bg-bg-hover hover:text-ink"
    >
      POD
    </Link>
  );
}

function friendlyBulkError(message: string | undefined): string {
  const m = message ?? '';
  if (m.includes('LYNRX_INSUFFICIENT_PERMISSIONS'))
    return 'You do not have permission to perform this bulk action.';
  if (m.includes('LYNRX_BULK_TENANT_MISMATCH'))
    return 'Selected orders span multiple tenants and cannot be batched.';
  if (m.includes('LYNRX_PHARMACY_NOT_IN_TENANT'))
    return "The target pharmacy isn't in the same tenant as the selected orders.";
  if (m.includes('LYNRX_PHARMACY_NOT_FOUND'))
    return 'Target pharmacy no longer accessible. Refresh.';
  if (m.includes('LYNRX_INVALID_REASON'))
    return 'Pick a valid cancel reason.';
  return 'Action failed. Try again.';
}

// Router-bound shell. Calls TanStack Router hooks and delegates render
// to the view. Lives behind createFileRoute so it never renders outside
// a router context.
function OrdersListPage(): JSX.Element {
  const search = Route.useSearch();
  const { status } = search;
  const driver = search.driver ?? 'all';
  const date = search.date ?? todayIso();
  const city = search.city ?? 'all';
  const zip = search.zip ?? 'all';
  const navigate = useNavigate();
  const setSearch = useCallback(
    (next: OrdersSearchPatch) => {
      // Build the clean URL search by-key — TanStack drops absent
      // optional fields, and exactOptionalPropertyTypes forbids
      // assigning `undefined` to an optional field. Use null in the
      // patch shape to mean "clear me."
      const clean: OrdersSearch = {
        status: next.status ?? search.status,
        date:
          'date' in next
            ? typeof next.date === 'string' && next.date
              ? next.date
              : todayIso()
            : search.date ?? todayIso(),
      };
      const driverNext = next.driver ?? search.driver;
      if (driverNext && driverNext !== 'all') clean.driver = driverNext;
      const cityNext = 'city' in next ? next.city : search.city;
      if (typeof cityNext === 'string' && cityNext && cityNext !== 'all') clean.city = cityNext;
      const zipNext = 'zip' in next ? next.zip : search.zip;
      if (typeof zipNext === 'string' && zipNext && zipNext !== 'all') clean.zip = zipNext;
      void navigate({ to: '/orders', search: clean });
    },
    [navigate, search],
  );
  const goToOrder = useCallback(
    (orderId: string) => {
      void navigate({
        to: '/orders/$orderId',
        params: { orderId },
        search: { status },
      });
    },
    [navigate, status],
  );
  const goToRoute = useCallback(
    (routeId: string) => {
      void navigate({ to: '/routes/$routeId', params: { routeId } });
    },
    [navigate],
  );
  return (
    <OrdersListView
      status={status}
      driver={driver}
      date={date}
      city={city}
      zip={zip}
      onSearchChange={setSearch}
      onRowOpen={goToOrder}
      onRouteCreated={goToRoute}
    />
  );
}

type PharmacyOption = { id: string; name: string; city: string | null };

type ViewProps = {
  status: StatusFilter;
  driver: string;
  date: string;
  city: string;
  zip: string;
  onSearchChange: (next: OrdersSearchPatch) => void;
  onRowOpen: (orderId: string) => void;
  onRouteCreated: (routeId: string) => void;
  // Test seam — defaults to the singleton supabase client.
  client?: LynrxSupabaseClient;
  // Test seam — defaults to global fetch. Tests inject a stub.
  fetchImpl?: typeof fetch;
};

// Pure render-and-fetch component. Owns the rows-state and pagination
// but takes the filter set + navigation callbacks from a parent so
// it can be unit-tested without a router.
export function OrdersListView({
  status,
  driver,
  date,
  city,
  zip,
  onSearchChange,
  onRowOpen,
  onRouteCreated,
  client = defaultClient,
  fetchImpl,
}: ViewProps): JSX.Element {
  const { session, isReady, userType, user } = useAuth();
  // Title only — page chrome stays restrained; the filter row carries
  // the working context (status / date range / driver) instead of a
  // subtitle that just labels the page.
  useSetPageHeader({ title: 'Orders' });
  const [rows, setRows] = useState<OrderListRow[] | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Infinite-scroll sentinel — rendered at the bottom of the list;
  // the observer effect below fetches the next page when it becomes
  // visible.
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Search text — local-only, not URL-bound. order_number is
  // PHI-adjacent per CLAUDE.md so it shouldn't appear in URL params
  // or telemetry.
  //
  // Two-stage search: order_number + patient_name (plaintext
  // transition shim on orders) still filter client-side over the
  // fetched page for fast typo-ing; a 2+ character term also fires
  // POST /api/patients/search which decrypts the encrypted
  // patients.full_name and returns matching patient IDs. Those IDs
  // narrow the orders SERVER query via .in('patient_id', ...) so a
  // pharmacist typing "smith" pulls Smith's orders across the whole
  // date-scoped set, not just the current fetched page.
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(searchText.trim()), 200);
    return () => window.clearTimeout(handle);
  }, [searchText]);
  // patientIds from /api/patients/search. null = search not active
  // (broad fetch), [] = search active but no name matches (empty
  // result), non-empty = narrow to these patient_ids.
  const [namePatientIds, setNamePatientIds] = useState<string[] | null>(null);
  useEffect(() => {
    if (!session || debouncedSearch.length < 2) {
      setNamePatientIds(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch('/api/patients/search', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ q: debouncedSearch }),
        });
        if (!res.ok) {
          if (!cancelled) setNamePatientIds([]);
          return;
        }
        const body = (await res.json().catch(() => null)) as
          | { ids: string[] }
          | null;
        if (cancelled) return;
        setNamePatientIds(body?.ids ?? []);
      } catch {
        if (!cancelled) setNamePatientIds([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, session]);

  // Driver dropdown options — fetched once. RLS scopes to the caller's
  // tenant; the page filters in-tenant only.
  const [drivers, setDrivers] = useState<DriverOption[] | null>(null);
  useEffect(() => {
    if (!isReady || !session) return;
    let cancelled = false;
    void (async () => {
      const { data, error: err } = await client
        .from('users')
        .select('id, full_name, email')
        .eq('user_type', 'driver')
        .is('deleted_at', null)
        .order('full_name', { ascending: true });
      if (cancelled) return;
      if (err) {
        captureSupabaseError(err, 'orders.list.fetch_drivers');
        setDrivers([]);
        return;
      }
      setDrivers((data ?? []) as DriverOption[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [client, isReady, session]);

  // Per-status counts for the working filter set (date + driver).
  // Independent of the current `status` filter so the strip shows the
  // shape of the user's whole working window.
  const [counts, setCounts] = useState<OrderCounts | null>(null);

  // Selection lives in route-local state.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Modal state for the bulk actions.
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [retrievalOpen, setRetrievalOpen] = useState(false); // Return to Pharmacy
  const [recallOpen, setRecallOpen] = useState(false);       // Retrieval from Customer

  // QuickView modal state — row click opens this instead of
  // navigating directly to the order detail page. Matches CNC's
  // portal preview pattern; "View full details" inside the modal
  // still routes to /orders/:id for the deep drill-in.
  const [quickViewOrderId, setQuickViewOrderId] = useState<string | null>(null);
  const [bulkFeedback, setBulkFeedback] = useState<
    { tone: 'success' | 'error'; text: string } | null
  >(null);

  // Caller's tenant_role for bulk reassign visibility. JWT only carries
  // user_type (PR #7 narrowed claims), so we fetch tenant_role from
  // public.users once at mount.
  const [tenantRole, setTenantRole] = useState<string | null>(null);
  useEffect(() => {
    if (!isReady || !session || !user) return;
    let cancelled = false;
    void (async () => {
      const { data, error: err } = await client
        .from('users')
        .select('tenant_role')
        .eq('id', user.id)
        .maybeSingle();
      if (cancelled) return;
      if (err) {
        captureSupabaseError(err, 'orders.list.fetch_tenant_role');
        setTenantRole(null);
        return;
      }
      setTenantRole(data?.tenant_role ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [client, isReady, session, user]);

  // Auto-clear bulk feedback after a few seconds — same cadence as
  // the route detail page driver dropdown.
  useEffect(() => {
    if (!bulkFeedback) return;
    const handle = window.setTimeout(() => setBulkFeedback(null), 5000);
    return () => window.clearTimeout(handle);
  }, [bulkFeedback]);

  // Translate a YYYY-MM-DD into the local-day's UTC ISO bounds for
  // .gte/.lte on orders.created_at. Local-day window is intentional —
  // dispatchers think in their own day, not UTC. If we ever ship to
  // multi-timezone tenants, this becomes a per-tenant tz lookup.
  const dateRangeIso = useMemo<{ from: string; to: string }>(() => {
    return {
      from: new Date(`${date}T00:00:00`).toISOString(),
      to: new Date(`${date}T23:59:59.999`).toISOString(),
    };
  }, [date]);

  // Yesterday's local-day bounds — fed into the stat-card delta query.
  const prevDayRangeIso = useMemo<{ from: string; to: string }>(() => {
    const d = new Date(`${date}T00:00:00`);
    d.setDate(d.getDate() - 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const prev = `${y}-${m}-${day}`;
    return {
      from: new Date(`${prev}T00:00:00`).toISOString(),
      to: new Date(`${prev}T23:59:59.999`).toISOString(),
    };
  }, [date]);

  const fetchPage = useCallback(
    async (
      nextPage: number,
      filter: StatusFilter,
      driverId: string,
      range: { from: string; to: string },
      cityFilter: string,
      zipFilter: string,
      // Optional server-side narrowing to a set of patient IDs
      // resolved by the encrypted-name search. null = no narrowing;
      // [] = search active but no name matches (returns zero rows
      // fast without a wasted round trip).
      patientIds: string[] | null = null,
    ) => {
      if (!session) return;
      setLoading(true);
      setError(null);
      // A 2+ character search that resolved to zero patient IDs
      // means the encrypted-name lookup found nothing — skip the
      // orders query entirely so the strip flips to the empty
      // state immediately. The client-side visibleRows filter is
      // suppressed downstream when patientIds is set, so we don't
      // need to preserve existing rows.
      if (patientIds !== null && patientIds.length === 0) {
        setRows([]);
        setHasMore(false);
        setPage(0);
        setLoading(false);
        return;
      }
      const from = nextPage * PAGE_SIZE;
      const to = from + PAGE_SIZE; // one past end to peek for next page
      const driverActive = driverId !== 'all';
      const cityActive = cityFilter !== 'all';
      const zipActive = zipFilter !== 'all';
      // Push the status filter into the Postgres query. RLS still scopes
      // by tenant_id; these filters narrow within the tenant's rows.
      let query = client
        .from('orders')
        .select(buildOrdersSelect(driverActive))
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (filter !== 'all') query = query.eq('status', filter);
      query = query.gte('created_at', range.from).lte('created_at', range.to);
      if (driverActive) {
        // Inner-join filter through the embed: only orders whose
        // dropoff stop is on a route assigned to this driver.
        query = query.eq('dropoff_stop.route.driver_id', driverId);
      }
      if (cityActive) query = query.eq('dropoff_stop.city', cityFilter);
      if (zipActive) query = query.eq('dropoff_stop.zip', zipFilter);
      if (patientIds !== null && patientIds.length > 0) {
        query = query.in('patient_id', patientIds);
      }
      const { data, error: err } = await query.range(from, to);
      setLoading(false);
      if (err) {
        captureSupabaseError(err, 'orders.list.fetch');
        setError('Could not load orders.');
        return;
      }
      const all = (data ?? []) as unknown as OrderListRow[];
      const pageRows = all.slice(0, PAGE_SIZE);
      // Infinite scroll: page 0 replaces (filter/date change or
      // initial load); later pages append. IntersectionObserver
      // below the table triggers page+1 as the user scrolls.
      setRows((prev) =>
        nextPage === 0 ? pageRows : [...(prev ?? []), ...pageRows],
      );
      setHasMore(all.length > PAGE_SIZE);
      setPage(nextPage);
    },
    [client, session],
  );

  // Per-status counts — shares the date + driver filters with the
  // page query but ignores the status filter so the strip shows the
  // shape of the whole working window. head:true skips row payload.
  const fetchCounts = useCallback(
    async (
      driverId: string,
      range: { from: string; to: string },
      cityFilter: string,
      zipFilter: string,
    ) => {
      if (!session) return;
      const driverActive = driverId !== 'all';
      const cityActive = cityFilter !== 'all';
      const zipActive = zipFilter !== 'all';
      const base = () => {
        let q = client
          .from('orders')
          .select(buildOrdersSelect(driverActive), { count: 'exact', head: true })
          .is('deleted_at', null)
          .gte('created_at', range.from)
          .lte('created_at', range.to);
        if (driverActive) q = q.eq('dropoff_stop.route.driver_id', driverId);
        if (cityActive) q = q.eq('dropoff_stop.city', cityFilter);
        if (zipActive) q = q.eq('dropoff_stop.zip', zipFilter);
        return q;
      };
      const [totalRes, deliveredRes, pendingRes, failedRes] = await Promise.all([
        base(),
        base().eq('status', 'delivered'),
        base().eq('status', 'pending'),
        // CNC's FAILED card: hard-failed only. The wider "needs
        // attention" set (attempted/returned/failed) still drives the
        // status filter dropdown, but the headline stat tracks failures.
        base().eq('status', 'failed'),
      ]);
      if (totalRes.error || deliveredRes.error || pendingRes.error || failedRes.error) {
        captureSupabaseError(
          totalRes.error ?? deliveredRes.error ?? pendingRes.error ?? failedRes.error,
          'orders.list.fetch_counts',
        );
        return;
      }
      setCounts({
        total: totalRes.count ?? 0,
        delivered: deliveredRes.count ?? 0,
        pending: pendingRes.count ?? 0,
        failed: failedRes.count ?? 0,
      });
    },
    [client, session],
  );

  // Yesterday's counts — same filter set (driver / city / zip), but the
  // prior-day range. Drives the day-over-day delta indicators in the
  // stat cards. Kept as its own state slice so card rendering can
  // distinguish "no data yet" from "0 deliveries yesterday."
  const [prevCounts, setPrevCounts] = useState<OrderCounts | null>(null);
  const fetchPrevCounts = useCallback(
    async (
      driverId: string,
      range: { from: string; to: string },
      cityFilter: string,
      zipFilter: string,
    ) => {
      if (!session) return;
      const driverActive = driverId !== 'all';
      const cityActive = cityFilter !== 'all';
      const zipActive = zipFilter !== 'all';
      const base = () => {
        let q = client
          .from('orders')
          .select(buildOrdersSelect(driverActive), { count: 'exact', head: true })
          .is('deleted_at', null)
          .gte('created_at', range.from)
          .lte('created_at', range.to);
        if (driverActive) q = q.eq('dropoff_stop.route.driver_id', driverId);
        if (cityActive) q = q.eq('dropoff_stop.city', cityFilter);
        if (zipActive) q = q.eq('dropoff_stop.zip', zipFilter);
        return q;
      };
      const [totalRes, deliveredRes, pendingRes, failedRes] = await Promise.all([
        base(),
        base().eq('status', 'delivered'),
        base().eq('status', 'pending'),
        base().eq('status', 'failed'),
      ]);
      if (totalRes.error || deliveredRes.error || pendingRes.error || failedRes.error) {
        // Soft-fail — the delta indicator is non-load-bearing.
        return;
      }
      setPrevCounts({
        total: totalRes.count ?? 0,
        delivered: deliveredRes.count ?? 0,
        pending: pendingRes.count ?? 0,
        failed: failedRes.count ?? 0,
      });
    },
    [client, session],
  );

  // Reset to page 0 whenever an active filter changes — old page index
  // is meaningless against a new filtered set. Also drop the selection,
  // since a different filter exposes a different working set.
  // `namePatientIds` is a filter too: null → broad fetch, [ids] →
  // narrow to those patient IDs (see fetchPage).
  useEffect(() => {
    if (!isReady || !session) return;
    setSelectedIds(new Set());
    setCreateError(null);
    setBulkFeedback(null);
    void fetchPage(0, status, driver, dateRangeIso, city, zip, namePatientIds);
    void fetchCounts(driver, dateRangeIso, city, zip);
    void fetchPrevCounts(driver, prevDayRangeIso, city, zip);
  }, [
    isReady,
    session,
    fetchPage,
    fetchCounts,
    fetchPrevCounts,
    status,
    driver,
    dateRangeIso,
    prevDayRangeIso,
    city,
    zip,
    namePatientIds,
  ]);

  // Infinite scroll — sentinel observer. Fires next-page fetch when
  // the bottom sentinel scrolls into view (rootMargin adds 200px so
  // the fetch starts slightly before the real end for a smoother
  // feel). Depends on hasMore/loading/page/filter args so the
  // observer rebinds correctly after each page appends.
  useEffect(() => {
    if (!hasMore || loading) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          void fetchPage(
            page + 1,
            status,
            driver,
            dateRangeIso,
            city,
            zip,
            namePatientIds,
          );
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [
    hasMore,
    loading,
    page,
    fetchPage,
    status,
    driver,
    dateRangeIso,
    city,
    zip,
    namePatientIds,
  ]);

  const toggleSelected = useCallback((id: string, isSelectable: boolean) => {
    if (!isSelectable) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setCreateError(null);
    setBulkFeedback(null);
  }, []);

  // Toggle the "select all" header checkbox over the currently-visible
  // selectable rows. If everything selectable is already selected, this
  // clears; otherwise it adds the missing ones.
  const selectableRows = useMemo(
    () => (rows ?? []).filter((r) => !TERMINAL_STATUSES.has(r.status)),
    [rows],
  );
  const allVisibleSelected =
    selectableRows.length > 0 &&
    selectableRows.every((r) => selectedIds.has(r.id));
  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) {
        // Drop only the visible selectable rows — preserve any prior
        // selections from other pages, though pagination resets selection
        // already, so this is mostly defensive.
        const next = new Set(prev);
        selectableRows.forEach((r) => next.delete(r.id));
        return next;
      }
      const next = new Set(prev);
      selectableRows.forEach((r) => next.add(r.id));
      return next;
    });
  }, [allVisibleSelected, selectableRows]);

  const doCreateRoute = useCallback(async () => {
    if (selectedIds.size === 0) return;
    if (!session) return;
    // Defense-in-depth: only pending rows should ever be in
    // selectedIds for the route-create path. Filter explicitly.
    const visiblePending = new Set(
      (rows ?? []).filter((r) => r.status === 'pending').map((r) => r.id),
    );
    const orderIds = [...selectedIds].filter((id) => visiblePending.has(id));
    if (orderIds.length === 0) {
      setCreateError('Create route requires at least one pending order in the selection.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const doFetch = fetchImpl ?? fetch;
      const res = await doFetch('/api/routes/create', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_ids: orderIds }),
      });
      if (!res.ok) {
        let code = 'create_failed';
        try {
          const body = (await res.json()) as { error?: string };
          if (typeof body.error === 'string') code = body.error;
        } catch {
          // ignore
        }
        Sentry.captureMessage('routes.create.non_2xx', {
          level: 'error',
          tags: { status: String(res.status), error_type: code },
        });
        setCreateError(friendlyCreateError(code, res.status));
        return;
      }
      const data = (await res.json()) as { route_id: string };
      if (!data.route_id) {
        setCreateError('Route created but no id returned.');
        return;
      }
      onRouteCreated(data.route_id);
    } catch (err) {
      captureSupabaseError(err, 'orders.list.create_route.network');
      setCreateError('Could not reach the server. Try again.');
    } finally {
      setCreating(false);
    }
  }, [fetchImpl, onRouteCreated, rows, selectedIds, session]);

  const selectedCount = selectedIds.size;
  const selectedOrderNumbers = useMemo(
    () =>
      (rows ?? [])
        .filter((r) => selectedIds.has(r.id))
        .map((r) => r.order_number),
    [rows, selectedIds],
  );
  // Statuses of the currently-selected rows, in the same order as
  // selectedOrderNumbers. Passed to BulkRetrievalModal so it can
  // show "N eligible · M will be skipped" upfront instead of the
  // driver finding out silently after clicking Mark.
  const selectedOrderStatuses = useMemo(
    () =>
      (rows ?? [])
        .filter((r) => selectedIds.has(r.id))
        .map((r) => r.status as string),
    [rows, selectedIds],
  );

  // Permission gates for the bulk action buttons. The RPCs also
  // enforce these via LYNRX_INSUFFICIENT_PERMISSIONS — these gates
  // hide the buttons so users don't see actions they can't take.
  //
  // Route creation is a DISPATCH authority — pharmacies don't run
  // drivers, so they don't create routes. Hiding the button from
  // pharmacy_admin / pharmacy_staff keeps their bulk-action bar
  // scoped to cancel / reassign / retrieval. platform_admin still
  // sees Create route via the LYN-staff override.
  const canCreateRoute =
    userType === 'dispatcher' || userType === 'platform_admin';
  const canBulkCancel =
    userType === 'pharmacy_admin' || userType === 'dispatcher';
  const canBulkRetrieve =
    userType === 'pharmacy_admin' || userType === 'dispatcher';
  const canBulkReassign =
    tenantRole === 'tenant_wide' || userType === 'platform_admin';

  // Pending count in current selection drives the Create route button's
  // enablement copy (it stays available for any selection, but errors
  // friendlier when no pending orders are picked).
  const pendingInSelectionCount = useMemo(
    () =>
      (rows ?? []).filter(
        (r) => r.status === 'pending' && selectedIds.has(r.id),
      ).length,
    [rows, selectedIds],
  );

  const columns: Column<OrderListRow>[] = useMemo(
    () => [
      {
        key: 'select',
        // Select-all checkbox in the header column.
        header: (
          <input
            type="checkbox"
            aria-label={
              allVisibleSelected
                ? 'Clear selection'
                : 'Select all visible orders'
            }
            checked={allVisibleSelected}
            disabled={selectableRows.length === 0}
            onChange={toggleSelectAll}
            className={
              selectableRows.length === 0
                ? 'h-4 w-4 cursor-not-allowed opacity-40'
                : 'h-4 w-4 cursor-pointer accent-primary'
            }
          />
        ) as unknown as string,
        className: 'w-10',
        render: (r) => {
          const isSelectable = !TERMINAL_STATUSES.has(r.status);
          return (
            <input
              type="checkbox"
              aria-label={
                isSelectable
                  ? `Select order ${r.order_number}`
                  : `Order ${r.order_number} is in a terminal state and cannot be acted on`
              }
              checked={selectedIds.has(r.id)}
              disabled={!isSelectable}
              onChange={() => toggleSelected(r.id, isSelectable)}
              className={
                isSelectable
                  ? 'h-4 w-4 cursor-pointer accent-primary'
                  : 'h-4 w-4 cursor-not-allowed opacity-40'
              }
            />
          );
        },
      },
      {
        key: 'order_number',
        header: 'Order #',
        sortable: true,
        sortValue: (r) => r.order_number,
        render: (r) => (
          <Link
            to="/orders/$orderId"
            params={{ orderId: r.id }}
            search={{ status }}
            className="text-primary hover:underline"
          >
            {r.order_number}
          </Link>
        ),
      },
      {
        key: 'patient_name',
        header: 'Patient',
        sortable: true,
        sortValue: (r) => r.patient_name?.toLowerCase() ?? null,
        render: (r) => (r.patient_name ? titleCase(r.patient_name) : '—'),
        className: 'whitespace-nowrap',
      },
      {
        key: 'address',
        header: 'Address',
        sortable: true,
        sortValue: (r) => r.dropoff_stop?.address?.toLowerCase() ?? null,
        render: addressDisplay,
        className: 'whitespace-nowrap',
      },
      {
        key: 'city',
        header: 'City',
        sortable: true,
        sortValue: (r) => r.dropoff_stop?.city?.toLowerCase() ?? null,
        render: cityDisplay,
        className: 'whitespace-nowrap',
      },
      {
        key: 'zip',
        header: 'ZIP',
        sortable: true,
        sortValue: (r) => r.dropoff_stop?.zip ?? null,
        render: zipDisplay,
        className: 'whitespace-nowrap',
      },
      {
        key: 'driver',
        header: 'Driver',
        sortable: true,
        sortValue: (r) => {
          const d = r.dropoff_stop?.route?.driver;
          if (!d) return null;
          return firstNameOf(d.full_name ?? d.email).toLowerCase();
        },
        render: driverDisplay,
        className: 'whitespace-nowrap',
      },
      {
        key: 'status',
        header: 'Status',
        sortable: true,
        sortValue: (r) => r.status,
        render: (r) => <StatusBadge entity="order" status={r.status} />,
      },
      {
        key: 'checks',
        header: 'Checks',
        sortable: true,
        // Sort by number of green chips (0-3). More evidence sorts
        // higher so scanning descending puts the audit-complete
        // rows at the top. Mirrors checksDisplay's logic verbatim so
        // the sort value matches what the driver sees on-screen.
        sortValue: (r) => {
          const pod = r.pod_records[0];
          const delivery = r.deliveries[0];
          if (!pod) return -1;
          let n = 0;
          if (delivery && !delivery.delivery_geofence_overridden) n += 1;
          if (pod.barcode_scanned) n += 1;
          if ((pod.photo_paths?.length ?? 0) > 0) n += 1;
          return n;
        },
        render: checksDisplay,
      },
      {
        key: 'time',
        header: 'Time',
        sortable: true,
        // Sort by raw ISO — same field timeDisplay uses.
        sortValue: (r) =>
          r.status === 'delivered' ? r.updated_at : r.created_at,
        render: timeDisplay,
        className: 'whitespace-nowrap',
      },
      {
        key: 'pod',
        header: 'POD',
        sortable: true,
        // 1 if a pod_records row exists, 0 otherwise. Sorting
        // descending groups all POD-complete rows together.
        sortValue: (r) => (r.pod_records.length > 0 ? 1 : 0),
        render: podDisplay,
      },
    ],
    [allVisibleSelected, selectableRows.length, selectedIds, status, toggleSelectAll, toggleSelected],
  );

  // Client-side search across the fetched page. Match against patient
  // name, address (street + city + zip), and order #. Case-insensitive,
  // substring. Empty query → no filter. Applied after the server query
  // so order #/PHI never end up in URL or in the network filter.
  const visibleRows = useMemo(() => {
    if (!rows) return rows;
    const q = debouncedSearch.toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [
        r.order_number,
        r.patient_name ?? '',
        r.dropoff_stop?.address ?? '',
        r.dropoff_stop?.city ?? '',
        r.dropoff_stop?.zip ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, debouncedSearch]);

  // Distinct cities + ZIPs from the visible page rows. Cheap and
  // accurate for any reasonable day's volume; if we ever ship to
  // pharmacies running 1000+ stops/day this becomes a separate
  // distinct query.
  const cityOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows ?? []) {
      const c = r.dropoff_stop?.city?.trim();
      if (c) set.add(c);
    }
    return Array.from(set).sort();
  }, [rows]);
  const zipOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows ?? []) {
      const z = r.dropoff_stop?.zip?.trim();
      if (z) set.add(z);
    }
    return Array.from(set).sort();
  }, [rows]);

  const hasActiveFilters =
    status !== 'all' ||
    driver !== 'all' ||
    city !== 'all' ||
    zip !== 'all' ||
    debouncedSearch.length > 0;

  return (
    <>
      {/* CNC-parity day stepper — single date, centered, with prev/next
          arrows. Drives the date filter on every query below. */}
      <DateStepper
        date={date}
        onChange={(next) => onSearchChange({ date: next })}
      />

      {/* Stat cards — four big numbers with day-over-day deltas. */}
      <StatRow counts={counts} prev={prevCounts} />

      {/* Progress bar — delivered / total for the visible day. */}
      <ProgressBar counts={counts} />

      {/* Search bar — prominent, full-width. Patient name, address,
          ZIP, or order #. Search text is local-only (PHI policy). */}
      <div className="mb-3">
        <input
          type="search"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search by patient name, address, ZIP, or order ID…"
          aria-label="Search orders"
          className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* Filter row — Driver / Status / City / ZIP. CNC-parity clean:
          no field labels above the selects (the placeholder text IS
          the label), single row with tight gap, matches the pharmacy
          portal on cncdelivery.com/portal/dashboard. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select
          value={driver}
          onChange={(e) => onSearchChange({ driver: e.target.value })}
          className="rounded-md border border-border bg-bg px-3 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Filter orders by driver"
        >
          <option value="all">All Drivers</option>
          {(drivers ?? []).map((d) => (
            <option key={d.id} value={d.id}>
              {firstNameOf(d.full_name ?? d.email)}
            </option>
          ))}
        </select>

        <select
          value={status}
          onChange={(e) => {
            const next = e.target.value;
            if (isStatusFilter(next)) onSearchChange({ status: next });
          }}
          className="rounded-md border border-border bg-bg px-3 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Filter orders by status"
        >
          <option value="all">All Statuses</option>
          {STATUS_FILTER_VALUES.filter((s) => s !== 'all').map((s) => (
            <option key={s} value={s}>
              {statusLabel(s)}
            </option>
          ))}
        </select>

        <select
          value={city}
          onChange={(e) => onSearchChange({ city: e.target.value })}
          className="rounded-md border border-border bg-bg px-3 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Filter orders by city"
        >
          <option value="all">All Cities</option>
          {cityOptions.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <select
          value={zip}
          onChange={(e) => onSearchChange({ zip: e.target.value })}
          className="rounded-md border border-border bg-bg px-3 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Filter orders by ZIP"
        >
          <option value="all">All ZIPs</option>
          {zipOptions.map((z) => (
            <option key={z} value={z}>
              {z}
            </option>
          ))}
        </select>

        {hasActiveFilters ? (
          <button
            type="button"
            onClick={() => {
              setSearchText('');
              onSearchChange({
                status: 'all',
                driver: 'all',
                city: null,
                zip: null,
              });
            }}
            className="text-xs font-medium text-primary hover:underline"
          >
            Clear filters
          </button>
        ) : null}
      </div>


      {error ? (
        <div className="mb-6">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {selectedCount > 0 ? (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-bg-soft px-3 py-1.5">
          <div className="text-xs text-ink">
            <span className="font-medium">{`${selectedCount} selected`}</span>
            <button
              type="button"
              onClick={clearSelection}
              className="ml-2 text-primary hover:underline"
              disabled={creating}
            >
              Clear
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {canCreateRoute ? (
              <Button onClick={doCreateRoute} disabled={creating} loading={creating}>
                Create route{pendingInSelectionCount > 0 ? ` (${pendingInSelectionCount} pending)` : ''}
              </Button>
            ) : null}
            {canBulkCancel ? (
              <Button variant="secondary" onClick={() => setCancelOpen(true)}>
                Cancel
              </Button>
            ) : null}
            {canBulkReassign ? (
              <Button variant="secondary" onClick={() => setReassignOpen(true)}>
                Reassign
              </Button>
            ) : null}
            {canBulkRetrieve ? (
              <Button variant="secondary" onClick={() => setRetrievalOpen(true)}>
                Return to Pharmacy
              </Button>
            ) : null}
            {canBulkRetrieve ? (
              <Button variant="secondary" onClick={() => setRecallOpen(true)}>
                Retrieval
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      {createError ? (
        <div className="mb-4">
          <Alert tone="error">{createError}</Alert>
        </div>
      ) : null}

      {bulkFeedback ? (
        <div className="mb-4">
          <Alert tone={bulkFeedback.tone === 'success' ? 'success' : 'error'}>
            {bulkFeedback.text}
          </Alert>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={visibleRows ?? []}
        rowKey={(r) => r.id}
        emptyMessage={
          rows === null
            ? 'Loading orders…'
            : debouncedSearch.length > 0 && (visibleRows?.length ?? 0) === 0
              ? `No orders match "${debouncedSearch}" on this page.`
              : status === 'pending'
                ? 'No pending orders. Upload a CSV to get started.'
                : `No orders with status "${statusLabel(status)}".`
        }
        onRowClick={(r) => setQuickViewOrderId(r.id)}
      />

      <OrderQuickView
        orderId={quickViewOrderId}
        onClose={() => setQuickViewOrderId(null)}
        onOpenFull={(id) => {
          setQuickViewOrderId(null);
          onRowOpen(id);
        }}
      />

      {/* Infinite-scroll sentinel — the IntersectionObserver
          registered above fetches the next page when this scrolls
          into view. Loading state shows "Loading more…"; end of
          list shows the running row count. */}
      <div
        ref={sentinelRef}
        className="mt-3 py-2 text-center text-xs text-ink-3"
      >
        {loading
          ? 'Loading more…'
          : visibleRows && visibleRows.length > 0
            ? hasMore
              ? ''
              : `End of list · ${visibleRows.length} order${visibleRows.length === 1 ? '' : 's'}`
            : ''}
      </div>

      <BulkCancelModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        orderIds={[...selectedIds]}
        orderNumbers={selectedOrderNumbers}
        client={client}
        onCompleted={(result) => {
          setCancelOpen(false);
          setBulkFeedback(result);
          if (result.tone === 'success') {
            clearSelection();
            void fetchPage(page, status, driver, dateRangeIso, city, zip, namePatientIds); void fetchCounts(driver, dateRangeIso, city, zip);
          }
        }}
      />

      <BulkReassignModal
        open={reassignOpen}
        onClose={() => setReassignOpen(false)}
        orderIds={[...selectedIds]}
        orderNumbers={selectedOrderNumbers}
        client={client}
        onCompleted={(result) => {
          setReassignOpen(false);
          setBulkFeedback(result);
          if (result.tone === 'success') {
            clearSelection();
            void fetchPage(page, status, driver, dateRangeIso, city, zip, namePatientIds); void fetchCounts(driver, dateRangeIso, city, zip);
          }
        }}
      />

      <BulkRetrievalModal
        open={retrievalOpen}
        onClose={() => setRetrievalOpen(false)}
        orderIds={[...selectedIds]}
        orderNumbers={selectedOrderNumbers}
        orderStatuses={selectedOrderStatuses}
        client={client}
        onCompleted={(result) => {
          setRetrievalOpen(false);
          setBulkFeedback(result);
          if (result.tone === 'success') {
            clearSelection();
            void fetchPage(page, status, driver, dateRangeIso, city, zip, namePatientIds); void fetchCounts(driver, dateRangeIso, city, zip);
          }
        }}
      />

      <BulkRecallModal
        open={recallOpen}
        onClose={() => setRecallOpen(false)}
        orderIds={[...selectedIds]}
        orderNumbers={selectedOrderNumbers}
        orderStatuses={selectedOrderStatuses}
        client={client}
        onCompleted={(result) => {
          setRecallOpen(false);
          setBulkFeedback(result);
          if (result.tone === 'success') {
            clearSelection();
            void fetchPage(page, status, driver, dateRangeIso, city, zip, namePatientIds); void fetchCounts(driver, dateRangeIso, city, zip);
          }
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Bulk modals — each renders its inputs, calls its RPC, surfaces the
// X-of-Y feedback via onCompleted.
// ---------------------------------------------------------------------------

type BulkFeedback = { tone: 'success' | 'error'; text: string };

const BULK_CANCEL_REASONS: { value: string; label: string }[] = [
  { value: 'customer_cancellation', label: 'Customer cancellation' },
  { value: 'duplicate',             label: 'Duplicate orders' },
  { value: 'pharmacy_error',        label: 'Pharmacy error' },
  { value: 'other',                 label: 'Other' },
];

function SelectedOrdersList({
  orderNumbers,
  max = 10,
}: {
  orderNumbers: string[];
  max?: number;
}): JSX.Element {
  const shown = orderNumbers.slice(0, max);
  const overflow = orderNumbers.length - shown.length;
  return (
    <div className="rounded-md border border-border bg-bg-soft px-3 py-2 text-xs text-ink-2">
      <p className="font-medium text-ink">
        {orderNumbers.length} selected order{orderNumbers.length === 1 ? '' : 's'}
      </p>
      <p className="mt-1 break-words">
        {shown.join(', ')}
        {overflow > 0 ? ` … +${overflow} more` : ''}
      </p>
    </div>
  );
}

function BulkCancelModal({
  open,
  onClose,
  orderIds,
  orderNumbers,
  client,
  onCompleted,
}: {
  open: boolean;
  onClose: () => void;
  orderIds: string[];
  orderNumbers: string[];
  client: LynrxSupabaseClient;
  onCompleted: (result: BulkFeedback) => void;
}): JSX.Element {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setNotes('');
      setSubmitError(null);
    }
  }, [open]);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!reason) {
        setSubmitError('Pick a reason.');
        return;
      }
      setSubmitting(true);
      setSubmitError(null);
      try {
        const { data, error } = await client.rpc('bulk_cancel_orders', {
          p_order_ids: orderIds,
          p_reason:    reason,
          // See cancel_order callsite — same regen drift on
          // bulk_cancel_orders.p_notes (nullable at runtime, dropped
          // by the regen). Cast preserves the null path.
          p_notes:     (notes.trim() === '' ? null : notes.trim()) as string,
        });
        if (error) {
          captureSupabaseError(error, 'orders.list.bulk_cancel');
          setSubmitError(friendlyBulkError(error.message));
          return;
        }
        const success = typeof data === 'number' ? data : Number(data ?? 0);
        const skipped = orderIds.length - success;
        onCompleted({
          tone: 'success',
          text:
            skipped > 0
              ? `${success} of ${orderIds.length} orders cancelled · ${skipped} skipped`
              : `${success} of ${orderIds.length} orders cancelled`,
        });
      } catch (err) {
        captureSupabaseError(err, 'orders.list.bulk_cancel.exception');
        setSubmitError(friendlyBulkError(undefined));
      } finally {
        setSubmitting(false);
      }
    },
    [client, notes, onCompleted, orderIds, reason],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cancel selected orders"
      titleId="bulk-cancel-title"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Keep orders
          </Button>
          <Button
            type="submit"
            form="bulk-cancel-form"
            variant="primary"
            disabled={submitting || !reason}
          >
            {submitting ? 'Cancelling…' : `Cancel ${orderIds.length} order${orderIds.length === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      <form id="bulk-cancel-form" onSubmit={onSubmit} className="space-y-3">
        <SelectedOrdersList orderNumbers={orderNumbers} />
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-ink">Reason</span>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Bulk cancel reason"
          >
            <option value="">— Select reason —</option>
            {BULK_CANCEL_REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-ink">Notes (optional)</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </label>
        {submitError ? <Alert tone="error">{submitError}</Alert> : null}
      </form>
    </Modal>
  );
}

function BulkReassignModal({
  open,
  onClose,
  orderIds,
  orderNumbers,
  client,
  onCompleted,
}: {
  open: boolean;
  onClose: () => void;
  orderIds: string[];
  orderNumbers: string[];
  client: LynrxSupabaseClient;
  onCompleted: (result: BulkFeedback) => void;
}): JSX.Element {
  const [pharmacies, setPharmacies] = useState<PharmacyOption[] | null>(null);
  const [targetId, setTargetId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPharmacies(null);
    setTargetId('');
    setSubmitError(null);
    void (async () => {
      const { data, error } = await client
        .from('pharmacies')
        .select('id, name, city')
        .is('deleted_at', null)
        .order('name', { ascending: true });
      if (cancelled) return;
      if (error) {
        captureSupabaseError(error, 'orders.list.fetch_reassign_pharmacies');
        setPharmacies([]);
        return;
      }
      setPharmacies(
        (data ?? []).map((p) => ({
          id: p.id,
          name: p.name,
          city: p.city ?? null,
        })),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [client, open]);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!targetId) {
        setSubmitError('Pick a destination pharmacy.');
        return;
      }
      setSubmitting(true);
      setSubmitError(null);
      try {
        const { data, error } = await client.rpc(
          'bulk_reassign_orders_to_pharmacy',
          { p_order_ids: orderIds, p_target_pharmacy_id: targetId },
        );
        if (error) {
          captureSupabaseError(error, 'orders.list.bulk_reassign');
          setSubmitError(friendlyBulkError(error.message));
          return;
        }
        const success = typeof data === 'number' ? data : Number(data ?? 0);
        const skipped = orderIds.length - success;
        onCompleted({
          tone: 'success',
          text:
            skipped > 0
              ? `${success} of ${orderIds.length} orders reassigned · ${skipped} skipped`
              : `${success} of ${orderIds.length} orders reassigned`,
        });
      } catch (err) {
        captureSupabaseError(err, 'orders.list.bulk_reassign.exception');
        setSubmitError(friendlyBulkError(undefined));
      } finally {
        setSubmitting(false);
      }
    },
    [client, onCompleted, orderIds, targetId],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Reassign to pharmacy"
      titleId="bulk-reassign-title"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="bulk-reassign-form"
            variant="primary"
            disabled={submitting || !targetId}
          >
            {submitting ? 'Reassigning…' : `Reassign ${orderIds.length} order${orderIds.length === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      <form id="bulk-reassign-form" onSubmit={onSubmit} className="space-y-3">
        <SelectedOrdersList orderNumbers={orderNumbers} />
        {pharmacies === null ? (
          <p className="text-sm text-ink-2">Loading pharmacies…</p>
        ) : pharmacies.length === 0 ? (
          <Alert tone="info">No pharmacies visible.</Alert>
        ) : (
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-ink">Destination pharmacy</span>
            <select
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label="Destination pharmacy"
            >
              <option value="">— Select pharmacy —</option>
              {pharmacies.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.city ? ` · ${p.city}` : ''}
                </option>
              ))}
            </select>
          </label>
        )}
        {submitError ? <Alert tone="error">{submitError}</Alert> : null}
      </form>
    </Modal>
  );
}

function BulkRetrievalModal({
  open,
  onClose,
  orderIds,
  orderNumbers,
  orderStatuses,
  client,
  onCompleted,
}: {
  open: boolean;
  onClose: () => void;
  orderIds: string[];
  orderNumbers: string[];
  orderStatuses: string[];
  client: LynrxSupabaseClient;
  onCompleted: (result: BulkFeedback) => void;
}): JSX.Element {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setSubmitError(null);
  }, [open]);

  // Eligibility precheck. Server-side gate is
  // bulk_mark_orders_for_retrieval's status filter (post-migration
  // 20260818000000: pending / assigned / out_for_delivery /
  // attempted / failed — i.e., the driver still has the package).
  // Delivered / cancelled / returned are terminal and use the new
  // recall action instead. Mirroring the filter here tells the
  // pharmacist upfront how many of their selection will actually
  // get flagged — no silent skips.
  const RETURN_STATUSES = useMemo(
    () =>
      new Set([
        'pending',
        'assigned',
        'out_for_delivery',
        'attempted',
        'failed',
      ]),
    [],
  );
  const eligibleCount = useMemo(
    () => orderStatuses.filter((s) => RETURN_STATUSES.has(s)).length,
    [orderStatuses, RETURN_STATUSES],
  );
  const skipCount = orderIds.length - eligibleCount;

  const onSubmit = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { data, error } = await client.rpc('bulk_mark_orders_for_retrieval', {
        p_order_ids: orderIds,
      });
      if (error) {
        captureSupabaseError(error, 'orders.list.bulk_retrieval');
        setSubmitError(friendlyBulkError(error.message));
        return;
      }
      const success = typeof data === 'number' ? data : Number(data ?? 0);
      const skipped = orderIds.length - success;
      onCompleted({
        tone: 'success',
        text:
          skipped > 0
            ? `${success} of ${orderIds.length} orders marked for retrieval · ${skipped} skipped`
            : `${success} of ${orderIds.length} orders marked for retrieval`,
      });
    } catch (err) {
      captureSupabaseError(err, 'orders.list.bulk_retrieval.exception');
      setSubmitError(friendlyBulkError(undefined));
    } finally {
      setSubmitting(false);
    }
  }, [client, onCompleted, orderIds]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Return to Pharmacy"
      titleId="bulk-retrieval-title"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={submitting || eligibleCount === 0}
          >
            {submitting
              ? 'Marking…'
              : eligibleCount === 0
                ? 'Nothing to mark'
                : `Mark ${eligibleCount} order${eligibleCount === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <SelectedOrdersList orderNumbers={orderNumbers} />
        {/* Upfront eligibility breakdown so the pharmacist sees what
            will and won't get marked BEFORE clicking. Attempted /
            failed are the only statuses the server-side RPC actually
            flips; everything else silently skips. */}
        <div className="rounded-md border border-border bg-bg-soft px-3 py-2 text-xs">
          <div className="text-ink">
            <span className="font-semibold">{orderIds.length}</span> selected
            {' · '}
            <span className={eligibleCount > 0 ? 'font-semibold text-signal-green' : 'text-ink-3'}>
              {eligibleCount} eligible
            </span>
            {skipCount > 0 ? (
              <>
                {' · '}
                <span className="text-ink-2">{skipCount} will be skipped</span>
              </>
            ) : null}
          </div>
          <div className="mt-1 text-ink-3">
            Return to Pharmacy applies when the driver still has the
            package — pending, assigned, out for delivery, attempted,
            or failed. Delivered orders needing recall from the
            customer use the separate Retrieval action.
          </div>
        </div>
        <p className="text-sm text-ink">
          Selected orders will be marked <span className="font-semibold">returned</span>{' '}
          and expected back at the pharmacy.
        </p>
        {submitError ? <Alert tone="error">{submitError}</Alert> : null}
      </div>
    </Modal>
  );
}

// -----------------------------------------------------------------------
// BulkRecallModal — Retrieval from Customer (post-delivery recall)
// -----------------------------------------------------------------------
// Sibling of BulkRetrievalModal ("Return to Pharmacy"); split into
// two actions because the physical location of the package differs.
// See migration 20260818000000_return_and_recall.sql for the semantic
// distinction and the underlying bulk_recall_delivered_orders RPC.

function BulkRecallModal({
  open,
  onClose,
  orderIds,
  orderNumbers,
  orderStatuses,
  client,
  onCompleted,
}: {
  open: boolean;
  onClose: () => void;
  orderIds: string[];
  orderNumbers: string[];
  orderStatuses: string[];
  client: LynrxSupabaseClient;
  onCompleted: (result: BulkFeedback) => void;
}): JSX.Element {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setSubmitError(null);
  }, [open]);

  // Eligibility precheck — mirrors the server-side filter in
  // bulk_recall_delivered_orders. Only delivered orders can be
  // recalled (order status is a historical fact; recall is tracked
  // as a separate audit event, not a status change).
  const eligibleCount = useMemo(
    () => orderStatuses.filter((s) => s === 'delivered').length,
    [orderStatuses],
  );
  const skipCount = orderIds.length - eligibleCount;

  const onSubmit = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { data, error } = await (client.rpc as unknown as (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: { message: string } | null }>)(
        'bulk_recall_delivered_orders',
        { p_order_ids: orderIds },
      );
      if (error) {
        captureSupabaseError(error, 'orders.list.bulk_recall');
        setSubmitError(friendlyBulkError(error.message));
        return;
      }
      const success = typeof data === 'number' ? data : Number(data ?? 0);
      const skipped = orderIds.length - success;
      onCompleted({
        tone: 'success',
        text:
          skipped > 0
            ? `${success} of ${orderIds.length} orders flagged for recall · ${skipped} skipped`
            : `${success} of ${orderIds.length} orders flagged for recall`,
      });
    } catch (err) {
      captureSupabaseError(err, 'orders.list.bulk_recall.exception');
      setSubmitError(friendlyBulkError(undefined));
    } finally {
      setSubmitting(false);
    }
  }, [client, onCompleted, orderIds]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Retrieval from Customer"
      titleId="bulk-recall-title"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={submitting || eligibleCount === 0}
          >
            {submitting
              ? 'Flagging…'
              : eligibleCount === 0
                ? 'Nothing to recall'
                : `Recall ${eligibleCount} order${eligibleCount === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <SelectedOrdersList orderNumbers={orderNumbers} />
        <div className="rounded-md border border-border bg-bg-soft px-3 py-2 text-xs">
          <div className="text-ink">
            <span className="font-semibold">{orderIds.length}</span> selected
            {' · '}
            <span
              className={
                eligibleCount > 0 ? 'font-semibold text-signal-green' : 'text-ink-3'
              }
            >
              {eligibleCount} eligible
            </span>
            {skipCount > 0 ? (
              <>
                {' · '}
                <span className="text-ink-2">{skipCount} will be skipped</span>
              </>
            ) : null}
          </div>
          <div className="mt-1 text-ink-3">
            Retrieval applies only to <span className="font-semibold">delivered</span>{' '}
            orders — the Rx is at the customer's location and needs
            to come back. For orders the driver still holds, use
            Return to Pharmacy instead.
          </div>
        </div>
        <p className="text-sm text-ink">
          Selected deliveries will be flagged for recall from the
          customer. The order stays marked delivered (that fact is
          preserved); the recall is recorded on the audit trail.
        </p>
        {submitError ? <Alert tone="error">{submitError}</Alert> : null}
      </div>
    </Modal>
  );
}

function friendlyCreateError(code: string, status: number): string {
  switch (code) {
    case 'no_orders':
      return 'Select at least one pending order before creating a route.';
    case 'duplicate_orders':
      return 'The same order appears more than once in the selection.';
    case 'invalid_orders':
      return 'One or more selected orders are not available. Refresh the list and try again.';
    case 'not_pending':
      return 'One or more selected orders are no longer pending. Refresh the list and try again.';
    case 'mixed_pharmacy':
      return 'A route can only contain orders from a single pharmacy.';
    case 'too_many_orders':
      return 'Too many orders selected — split into multiple routes.';
    case 'invalid_json':
    case 'order_ids_required':
      return 'Something went wrong building the request. Refresh and try again.';
    default:
      return `Could not create route (${code}${status > 0 ? `, HTTP ${status}` : ''}).`;
  }
}

// Small uppercase-label wrapper for each filter control. Matches the
// CNC pattern (label above the control, tiny tracked uppercase).
function FilterField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-medium uppercase tracking-wide text-ink-2">
        {label}
      </span>
      {children}
    </label>
  );
}

// ---------------------------------------------------------------------------
// CNC-parity headline: date stepper + stat cards + progress bar.
// ---------------------------------------------------------------------------

function parseIsoLocal(iso: string): Date {
  const parts = iso.split('-');
  return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
}

function fmtIsoLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fullDateLabel(iso: string): string {
  return parseIsoLocal(iso).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function DateStepper({
  date,
  onChange,
}: {
  date: string;
  onChange: (next: string) => void;
}): JSX.Element {
  const back = (): void => {
    const d = parseIsoLocal(date);
    d.setDate(d.getDate() - 1);
    onChange(fmtIsoLocal(d));
  };
  const forward = (): void => {
    const d = parseIsoLocal(date);
    d.setDate(d.getDate() + 1);
    onChange(fmtIsoLocal(d));
  };
  const today = todayIso();
  const isToday = date === today;
  return (
    <div className="mb-3 flex items-center justify-center gap-2">
      <button
        type="button"
        onClick={back}
        aria-label="Previous day"
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-bg text-ink-2 hover:bg-bg-hover"
      >
        ‹
      </button>
      <span className="inline-flex min-w-[240px] items-center justify-center rounded-lg bg-bg px-3 py-1.5 text-[15px] font-bold text-ink">
        {fullDateLabel(date)}
      </span>
      <button
        type="button"
        onClick={forward}
        aria-label="Next day"
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-bg text-ink-2 hover:bg-bg-hover"
      >
        ›
      </button>
      {/* CNC-parity "Today" jump button — brand-tinted pill next to
          the stepper. Disabled when already on today so it can't be
          spammed as a no-op. See cnc-web PortalShell.jsx ~line 756. */}
      <button
        type="button"
        onClick={() => onChange(today)}
        disabled={isToday}
        aria-label="Jump to today"
        className={
          isToday
            ? 'ml-1 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold text-ink-3'
            : 'ml-1 inline-flex items-center rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold text-primary hover:bg-primary/25'
        }
      >
        Today
      </button>
      {/* Email Summary — CNC parity. Sends a formatted daily digest
          (KPI counts + completion % + top-line status) to the caller's
          email. v0 preview: button + toast placeholder; wire the
          actual endpoint in a follow-up PR once we've decided on
          template + recipient logic (self only vs whole-tenant). */}
      <button
        type="button"
        onClick={() => {
          window.alert(
            'Email Summary — coming soon. This will email today\'s KPI digest to your account.',
          );
        }}
        className="ml-1 inline-flex items-center rounded-lg bg-[#0B1E3D] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1a2e52]"
      >
        Email Summary
      </button>
    </div>
  );
}

function StatRow({
  counts,
  prev,
}: {
  counts: OrderCounts | null;
  prev: OrderCounts | null;
}): JSX.Element {
  return (
    <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatCard
        label="Total"
        value={counts?.total ?? 0}
        prev={prev?.total ?? null}
        valueClassName="text-ink"
      />
      <StatCard
        label="Delivered"
        value={counts?.delivered ?? 0}
        prev={prev?.delivered ?? null}
        valueClassName="text-signal-green"
      />
      <StatCard
        label="Pending"
        value={counts?.pending ?? 0}
        prev={prev?.pending ?? null}
        valueClassName="text-signal-amber"
      />
      <StatCard
        label="Failed"
        value={counts?.failed ?? 0}
        prev={prev?.failed ?? null}
        valueClassName="text-signal-red"
        // For "Failed", down is good — invert the delta tone so a
        // drop reads as positive (green) and a rise reads as negative
        // (red).
        invertDeltaTone
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  prev,
  valueClassName,
  invertDeltaTone = false,
}: {
  label: string;
  value: number;
  prev: number | null;
  valueClassName: string;
  invertDeltaTone?: boolean;
}): JSX.Element {
  const delta = prev !== null ? value - prev : null;
  const pct =
    prev !== null && prev > 0
      ? Math.round((delta! / prev) * 100)
      : prev === 0 && value === 0
      ? 0
      : null;
  return (
    <div className="flex flex-col rounded-[10px] border border-border bg-bg px-4 py-3">
      <div className="text-[11px] font-medium uppercase tracking-wide text-ink-3">
        {label}
      </div>
      <div className={'mt-1 text-2xl font-bold leading-none ' + valueClassName}>
        {value}
      </div>
      <div className="mt-1 text-[11px]">{renderDelta(delta, pct, invertDeltaTone)}</div>
    </div>
  );
}

function renderDelta(
  delta: number | null,
  pct: number | null,
  invert: boolean,
): JSX.Element {
  if (delta === null) {
    return <span className="text-ink-3">—</span>;
  }
  if (delta === 0) {
    return <span className="text-ink-3">— same</span>;
  }
  const up = delta > 0;
  const goodWhenUp = !invert;
  const tone = up === goodWhenUp ? 'text-signal-green' : 'text-signal-red';
  const arrow = up ? '▲' : '▼';
  // CNC pharmacy portal shows percentage deltas (▼15%). Raw-count
  // fallback only when prev=0 and we can't compute a percentage (any
  // change from 0 is undefined %). Matches
  // cncdelivery.com/portal/dashboard exactly.
  const label = pct !== null ? `${Math.abs(pct)}%` : String(Math.abs(delta));
  // No space between arrow + value — matches CNC's ▼15% compact form.
  return (
    <span className={tone}>
      {arrow}
      {label}
    </span>
  );
}

function ProgressBar({ counts }: { counts: OrderCounts | null }): JSX.Element | null {
  if (!counts) return null;
  if (counts.total === 0) return null;
  const pct = Math.round((counts.delivered / counts.total) * 100);
  return (
    <div className="mb-3">
      {/* Track uses neutral-200 (#e5e5e5) — the design-system bg-soft
          (#F3F4F6) is the same tone as the page background so the bar
          disappeared. Neutral-200 is one step darker and pops the
          fill without competing with the border tokens. */}
      <div className="h-2 w-full overflow-hidden rounded-md bg-neutral-200">
        <div
          className="h-full rounded-md bg-signal-green transition-[width]"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-1.5 text-xs text-ink-3">
        <span className="font-semibold text-ink-2">{pct}%</span> Complete ·{' '}
        {counts.delivered} of {counts.total} delivered today
      </div>
    </div>
  );
}
